import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ListTodo,
  Users,
  Settings,
  LayoutGrid,
  User,
  FileText,
  Layers,
  UserCog,
  Clock,
  Calendar,
  BarChart2,
  LayoutDashboard,
  FileBarChart2,
  Briefcase,
  Tag,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useSelector } from "react-redux";
import "./styles.scss";
import { AnimatePresence, motion } from "framer-motion";

const Sidebar = ({ isExpanded, setIsExpanded }) => {
  const [cookies] = useCookies(["euid", "eun", "eut"]);
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [finalMenu, setFinalMenu] = useState([]);
  const accessData = useSelector((state) => state.accessDetails?.accessData);

  const isAdmin = accessData?.role?.label === "Admin";
  const teamlead = accessData?.leadingTeams.length > 0;
  const hasClientDashboardAccess =
    accessData?.access?.find(
      (module) => module.module === "Client Dashboard"
    ) || {};
  const teamDsr =
    accessData?.access?.find((module) => module.module === "Team DSR Report") ||
    {};
  const teamtask =
    accessData?.access?.find((module) => module.module === "Tasks") || {};
  const myDsr =
    accessData?.access?.find((module) => module.module === "My DSR Report") ||
    {};
  const canViewClientDash = isAdmin || hasClientDashboardAccess?.is_view;
  const canteamdsr = isAdmin || teamDsr?.is_view;
  const canseemytask = isAdmin || teamtask?.is_view;

  const navigate = useNavigate();
  const location = useLocation();

  const menuGroups = [
    {
      title: "ANALYZE",
      items: [
        {
          id: 3,
          icon: <LayoutDashboard size={18} />,
          title: "Dashboard",
          path: "/dashboard",
        },
        {
          id: 4,
          icon: <FileBarChart2 size={18} />,
          title: "Reports",
          path: null,
          subItems: [
            ...(canViewClientDash
              ? [
                  {
                    id: 41,
                    title: "Client Dashboard",
                    path: "/dashboard/client-report",
                  },
                ]
              : []),
            { id: 42, title: "Summary", path: "/dashboard/reports" },
            {
              id: 53,
              title: "Project Reports",
              path: "/dashboard/project-reports",
            },
            {
              id: 43,
              title: "My DSR Report",
              path: "/dashboard/daily-status-report",
            },
            ...(isAdmin || canteamdsr
              ? [
                  {
                    id: 44,
                    title: "Team DSR Report",
                    path: "/dashboard/team-status-report",
                  },
                ]
              : []),
          ],
        },
      ],
    },
    {
      title: "MANAGE",
      items: [
        {
          id: 5,
          icon: <ListTodo size={18} />,
          title: "Tasks",
          path: null,
          subItems: [
            { id: 52, title: "My Tasks", path: "/dashboard/my-tasks" },
            ...(isAdmin || canseemytask
              ? [{ id: 51, title: "My Team Tasks", path: "/dashboard/tasks" }]
              : []),
          ],
        },
        {
          id: 6,
          icon: <Layers size={18} />,
          title: "Projects",
          path: "/dashboard/projects",
        },
        {
          id: 8,
          icon: <User size={18} />,
          title: "Employee Management",
          path: "/dashboard/employees",
        },
        {
          id: 7,
          icon: <Users size={18} />,
          title: "Team Management",
          path: "/dashboard/teams",
        },
        {
          id: 9,
          icon: <UserCog size={18} />,
          title: "Client Management",
          path: "/dashboard/clientmanagement",
        },
      ],
    },
    {
      title: "SETTINGS",
      items: [
        {
          id: 11,
          icon: <Settings size={18} />,
          title: "Settings",
          path: "/dashboard/settings",
        },
      ],
    },
  ];

  // Auto-expand submenu if one of its items matches the current path
  useEffect(() => {
    menuGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (
          item.subItems &&
          item.subItems.some((subItem) => location.pathname === subItem.path)
        ) {
          if (!expandedMenus.includes(item.id)) {
            setExpandedMenus((prev) => [...prev, item.id]);
          }
        }
      });
    });
  }, [location.pathname]);

  useEffect(() => {
    if (accessData) {
      const getFilteredMenuItems = (role, accessList) => {
        if (role?.label === "Admin") return menuGroups;

        const accessibleModules = accessList
          ?.filter((item) => item.is_view)
          .map((item) => item.module);

        const hasSettingsAccess = accessList?.some(
          (item) =>
            (item.module === "Role Management" ||
              item.module === "Department") &&
            item.is_view
        );

        return menuGroups
          .map((group) => ({
            ...group,
            items: group.items.filter((item) => {
              if (item.title === "Settings") return hasSettingsAccess;
              return accessibleModules?.includes(item.title);
            }),
          }))
          .filter((group) => group.items.length > 0);
      };
      setFinalMenu(getFilteredMenuItems(accessData?.role, accessData?.access));
    } else {
      setFinalMenu([]);
    }
  }, [accessData]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleSubmenu = (itemId) => {
    setExpandedMenus((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleNavigation = (path) => {
    if (path) navigate(path);
  };

  const handlegohome = () => {
    navigate("/dashboard");
  };

  // Animation variants for smooth transitions
  const submenuVariants = {
    hidden: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className={`sidebar ${!isExpanded ? "collapsed" : ""}`}>
      <button className="toggle-button" onClick={toggleSidebar}>
        {isExpanded ? (
          <ChevronLeft size={16} color="#8f95b2" />
        ) : (
          <ChevronRight size={16} color="#8f95b2" />
        )}
      </button>

      <div
        className="logo-section"
        style={{ cursor: "pointer" }}
        onClick={() => handlegohome()}
      >
        <img src="/logo.svg" alt="brand_logo" height={30} />
        {isExpanded && <h1 className="text">Task M.</h1>}
      </div>

      <nav className="nav-menu">
        <AnimatePresence>
          {finalMenu.map((group) => (
            <div key={group.title} className="menu-group">
              {isExpanded && (
                <motion.div
                  className="group-title"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {group.title}
                </motion.div>
              )}
              {group.items.map((item) => {
                const isDirectlyActive =
                  item.path && location.pathname === item.path;
                const hasActiveSubItem = item.subItems?.some(
                  (subItem) => location.pathname === subItem.path
                );
                const hasSubItems = item.subItems && item.subItems.length > 0;
                const isSubmenuExpanded = expandedMenus.includes(item.id);

                return (
                  <motion.div
                    key={item.id}
                    className="menu-item"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      className={`menu-item-header ${
                        isDirectlyActive ? "active" : ""
                      } ${hasActiveSubItem ? "has-active-subitem" : ""}`}
                      onClick={() => {
                        if (hasSubItems) {
                          toggleSubmenu(item.id);
                        } else {
                          handleNavigation(item.path);
                        }
                      }}
                    >
                      <div className="icon-title-wrapper">
                        {item.icon}
                        {isExpanded && (
                          <motion.span className="menu-title">
                            {item.title}
                          </motion.span>
                        )}
                      </div>
                      {hasSubItems && isExpanded && (
                        <div className="submenu-chevron">
                          {isSubmenuExpanded ? (
                            <ChevronUp
                              size={14}
                              color={isDirectlyActive ? "#fff" : "#8f95b2"}
                            />
                          ) : (
                            <ChevronDown
                              size={14}
                              color={isDirectlyActive ? "#fff" : "#8f95b2"}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {hasSubItems && isExpanded && (
                      <AnimatePresence initial={false}>
                        {isSubmenuExpanded && (
                          <motion.div
                            className="submenu"
                            variants={submenuVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                          >
                            {item.subItems.map((subItem) => {
                              const isSubItemActive =
                                location.pathname === subItem.path;

                              return (
                                <div
                                  key={subItem.id}
                                  className={`submenu-item ${
                                    isSubItemActive ? "active" : ""
                                  }`}
                                  onClick={() => handleNavigation(subItem.path)}
                                >
                                  <span className="submenu-title">
                                    {subItem.title}
                                  </span>
                                </div>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ))}
        </AnimatePresence>
      </nav>
    </div>
  );
};

export default Sidebar;
